import React, {FC, useContext, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {useMouseDrag} from '@/common/helpers/hooks/useMosueDrag';
import {useLocalizer} from '@/common/services/localizer';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {informerCodeGenerator, informerCssUrlGenerator} from '@/informers/services/informerGenerator/informerGenerator';
import {ServicesContext} from '@/informers/contexts/ServicesContext';
import {getCssColor} from '@/common/components/ColorPicker';
import {PreviewBackgroundPicker} from '../PreviewBackgroundPicker';
import styles from './Preview.css';

// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __sinInformerFill: () => void;

export const Preview: FC = () => {
	const t = useLocalizer();
	const {state, dispatch} = useInformerConfigState();
	const {informersAPI} = useContext(ServicesContext)!;

	const [backgroundColor, setBackgroundColor] = useState({r: 246, g: 246, b: 246, a: 1});

	const mainNodeRef = useRef<HTMLDivElement>(null);
	const contentNodeRef = useRef<HTMLDivElement>(null);

	// Width dnd config
	const [previewWidth, setPreviewWidth] = useState(1);

	const widthChangeDrag = useMouseDrag({
		minimumXDistance: 4,
		onMove(event) {
			const {offsetLeft: parentOffset, clientWidth: parentWidth} = mainNodeRef.current!;
			const parentOffsetMiddle = parentOffset + parentWidth / 2;
			const parentPointerOffset = event.pageX - parentOffsetMiddle;
			let newWidth = parentPointerOffset / (parentWidth / 2);
			newWidth = Math.min(newWidth, 1);
			newWidth = Math.max(newWidth, 0);
			setPreviewWidth(newWidth);
		},
	});

	// Inject an informer css
	useEffect(() => {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = informerCssUrlGenerator();
		document.head.appendChild(link);

		return () => {
			document.head.removeChild(link);
		};
	}, []);

	const prevScript = useRef<HTMLScriptElement | null>(null);
	const prevState = useRef(state);

	// Render informed content and inject (and re-inject on locations change) the informer script to fill the data
	useEffect(() => {
		if (state.locations.length === 0) {
			return;
		}

		// Just rewrite the informer preview when no locations changed
		if (state.locations === prevState.current.locations && typeof __sinInformerFill !== 'undefined') {
			contentNodeRef.current!.innerHTML = informerCodeGenerator(state);
			__sinInformerFill();
			return;
		}

		// Remove a previous script
		if (prevScript.current) {
			document.head.removeChild(prevScript.current);
		}

		// Fetch the new script url
		informersAPI
			.makeInformer(
				state.locations.map(item => item.id),
				`informer-constructor@${DOMAIN_NAME}`,
			)
			.then(({url}) => {
				// Inject a new one script and rewrite the informer after it loads
				const script = document.createElement('script');
				script.src = `${url}&render=manual`;
				script.onload = () => {
					contentNodeRef.current!.innerHTML = informerCodeGenerator(state);
					__sinInformerFill();
				};
				document.head.appendChild(script);
				prevScript.current = script;
			});
	}, [state]);

	// Fetch locations on a language change to update it's titles
	useEffect(() => {
		if (prevState.current.language === state.language) {
			return;
		}
		informersAPI
			.getLocationTitles(
				state.locations.map(item => item.id),
				state.language,
			)
			.then(locations => {
				dispatch({
					type: 'setValues',
					values: {
						locations,
					},
				});
			});
	}, [state.language]);

	useEffect(() => {
		prevState.current = state;
	}, [state]);

	return (
		<div className={styles.root} style={{backgroundColor: getCssColor(backgroundColor)}}>
			<div ref={mainNodeRef}>
				<div
					className={styles.resizable}
					style={state.sizeType === 'adaptive' ? {width: `${previewWidth * 100}%`} : undefined}>
					<div className={styles.scrollable}>
						{state.locations.length !== 0 ? (
							<div
								ref={contentNodeRef}
								className={cn(styles.content, state.sizeType === 'adaptive' && styles.isWide)}
							/>
						) : (
							<div className={styles.placeholder}>{t('resultPlaceholder')}</div>
						)}
					</div>
					{state.locations.length !== 0 && state.sizeType === 'adaptive' && (
						<div className={styles.widthChangeTarget} onMouseDown={widthChangeDrag.handleMouseDown} />
					)}
				</div>
			</div>

			<PreviewBackgroundPicker value={backgroundColor} onChange={setBackgroundColor} />
		</div>
	);
};

Preview.displayName = 'Preview';
